// src/pages/mushroom-products-page.tsx

import Button from "@mui/material/Button";
import React, { useEffect, useState, useContext, useRef } from "react";
import { useHistory } from "react-router-dom";
import {
  GridColDef,
  GridRowSelectionModel,
  GridPaginationModel,
  GridToolbar,
  useGridApiRef,
  GridRowParams,
  GridRowClassNameParams,
  GridDensity,
  GridInitialState,
  GRID_CHECKBOX_SELECTION_COL_DEF,
} from "@mui/x-data-grid";
import {
  CircularProgress,
  IconButton,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  OutlinedInput,
} from "@mui/material";

import DeleteIcon from "@mui/icons-material/Delete";
import Page from "../components/page";
import LoadingBackdrop from "../components/loading-backdrop";
import ConfirmDialog from "../components/confirm-dialog";
import { ErrorContext, UserContext } from "../components/app-routes";
import { ActiveFilterControls } from "../components/active-filter-controls";
import mixpanel from "mixpanel-browser";
import { getSearchParamNumber } from "../utils/history";
import { StripedDataGrid } from "../components/striped-data-grid";
import { DateTime } from "luxon";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";

type MushroomProduct = {
  id: number;
  name: string;
  size: "small" | "medium" | "large" | "";
  weight: number | null;
  weightUnit: "g" | "lb";
  strainNames: string[];
  notes: string | null;
  archivedAt: string | null;
};

// ----- Local storage keys (MVP/local) -----
const mushroomProductsStorageKey = "mushroomProducts";
const mushroomProductsGridStateStorageKey = "mushroomProductsGridState";
const mushroomProductsGridDensityStorageKey = "mushroomProductsGridDensity";
const showArchivedCheckedStorageKey = "mushroomProductsShowArchivedChecked";
const showActiveCheckedStorageKey = "mushroomProductsShowActiveChecked";

// ----- Local storage helpers (MVP/local) -----
function loadMushroomProducts(): MushroomProduct[] {
  const raw = window.localStorage.getItem(mushroomProductsStorageKey);
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed as MushroomProduct[]) : [];
  } catch {
    return [];
  }
}

function saveMushroomProducts(products: MushroomProduct[]) {
  window.localStorage.setItem(
    mushroomProductsStorageKey,
    JSON.stringify(products),
  );
}

function trashMushroomProduct(id: number) {
  const products = loadMushroomProducts();
  const now = DateTime.local().toISO();
  const next = products.map((p) =>
    p.id === id ? { ...p, archivedAt: now } : p,
  );
  saveMushroomProducts(next);
}

// ----- DataGrid columns -----
const columns: GridColDef[] = [
  {
    ...GRID_CHECKBOX_SELECTION_COL_DEF,
    hideable: false,
    headerName: "Checkbox",
  },
  {
    field: "archived",
    headerName: "",
    width: 35,
    minWidth: 35,
    sortable: false,
    filterable: false,
    align: "center",
    disableColumnMenu: true,
    disableExport: true,
    renderCell: (params) =>
      params.row.archivedAt ? (
        <Inventory2OutlinedIcon fontSize="small" titleAccess="Archived" />
      ) : null,
  },
  {
    field: "name",
    headerName: "Name",
    width: 400,
  },
  {
    field: "size",
    headerName: "Size",
    width: 140,
  },
  {
    field: "weight",
    headerName: "Weight",
    width: 140,
  },
  {
    field: "strains",
    headerName: "Strains",
    flex: 1,
  },
  {
    field: "notes",
    headerName: "Notes",
    width: 200,
  },
];

// ----- Grid component -----
const ProductsDataGrid = React.memo(
  (props: {
    initialPage: number | undefined;
    onPaginationModelChange: (model: GridPaginationModel) => void;
    products: MushroomProduct[] | null | undefined;
    onSelectionChange: (params: GridRowSelectionModel) => void;
  }) => {
    const history = useHistory();
    const apiRef = useGridApiRef();
    const [initialState, setInitialState] = React.useState<GridInitialState>();

    const [paginationModel, setPaginationModel] =
      React.useState<GridPaginationModel>(() => {
        const raw = localStorage.getItem(mushroomProductsGridStateStorageKey);

        if (raw) {
          try {
            const parsed = JSON.parse(raw);
            const saved = parsed?.pagination?.paginationModel;

            if (saved?.pageSize) {
              return {
                page: props.initialPage ?? saved.page ?? 0,
                pageSize: saved.pageSize,
              };
            }
          } catch {}
        }

        return {
          page: props.initialPage ?? 0,
          pageSize: 15,
        };
      });

    const saveSnapshot = React.useCallback(() => {
      if (apiRef?.current?.exportState) {
        const currentState = apiRef.current.exportState();

        if (!(currentState as any)?.pagination?.paginationModel?.pageSize) {
          (currentState as any).pagination = {
            paginationModel: { page: 0, pageSize: 15 },
          };
        }

        localStorage.setItem(
          mushroomProductsGridStateStorageKey,
          JSON.stringify(currentState),
        );
      }
    }, [apiRef]);

    React.useLayoutEffect(() => {
      const raw = localStorage?.getItem(mushroomProductsGridStateStorageKey);
      let parsed: GridInitialState | undefined;

      if (raw) {
        try {
          parsed = JSON.parse(raw);
        } catch {
          localStorage.removeItem(mushroomProductsGridStateStorageKey);
        }
      }

      setInitialState({
        ...parsed,
        columns: {
          columnVisibilityModel: {
            notes: false,
            ...(parsed?.columns?.columnVisibilityModel ?? {}),
          },
          orderedFields: [],
        },
        filter: undefined,
        sorting: parsed?.sorting ?? {
          sortModel: [{ field: "name", sort: "asc" }],
        },
        pagination: {
          paginationModel: {
            page:
              (parsed as any)?.pagination?.paginationModel?.page ??
              props.initialPage ??
              0,
            pageSize:
              (parsed as any)?.pagination?.paginationModel?.pageSize ?? 15,
          },
        },
      });

      window.addEventListener("beforeunload", saveSnapshot);

      return () => {
        window.removeEventListener("beforeunload", saveSnapshot);
        saveSnapshot();
      };
    }, [saveSnapshot, props.initialPage]);

    const [density, setDensity] = React.useState<GridDensity>(() => {
      const d = localStorage.getItem(
        mushroomProductsGridDensityStorageKey,
      ) as GridDensity | null;
      return d === "compact" || d === "comfortable" || d === "standard"
        ? d
        : "standard";
    });

    const context = useContext(UserContext);
    const grower = context.grower;

    if (!grower) {
      throw new Error("Invalid context");
    }

    const csvFileName = React.useMemo(() => {
      const zone =
        (grower as any)?.TimeZone ||
        (grower as any)?.timeZone ||
        DateTime.local().zoneName;

      const now = DateTime.local().setZone(zone);

      return `SeedLeaf Mushroom Products List - ${now.toFormat("MMM dd, yyyy")}`;
    }, [grower]);

    if (!props.products) {
      return null;
    }

    if (!initialState) {
      return <CircularProgress />;
    }

    return (
      <StripedDataGrid
        apiRef={apiRef}
        columns={columns}
        rows={props.products.map((p) => ({
          id: p.id,
          size: p.size,
          weight:
            p.weight !== null && p.weight !== undefined
              ? `${p.weight} ${p.weightUnit}`
              : "",

          name: p.name,
          strains: (p.strainNames ?? []).join(", "),
          notes: p.notes ?? "",
          archivedAt: p.archivedAt,
        }))}
        initialState={{
          ...initialState,
          filter: undefined,
          sorting: {
            sortModel: [
              {
                field: "name",
                sort: "asc",
              },
            ],
          },
        }}
        checkboxSelection
        onRowSelectionModelChange={props.onSelectionChange}
        pagination
        paginationMode="client"
        pageSizeOptions={[15, 25, 50, 100]}
        paginationModel={paginationModel}
        onPaginationModelChange={(m) => {
          setPaginationModel(m);
          props.onPaginationModelChange(m);
        }}
        slots={{
          toolbar: GridToolbar,
        }}
        density={density}
        onDensityChange={(d) => {
          setDensity(d);
          localStorage.setItem(mushroomProductsGridDensityStorageKey, d);
        }}
        showToolbar
        slotProps={{
          toolbar: {
            showQuickFilter: true,
            csvOptions: {
              allColumns: true,
              fileName: csvFileName,
            },
          },
        }}
        getRowClassName={(params: GridRowClassNameParams) => {
          const row = params.row as any;
          if (row.archivedAt) {
            return "archived-row";
          }
          return params.indexRelativeToCurrentPage % 2 === 0 ? "even" : "odd";
        }}
        disableRowSelectionOnClick
        onRowClick={(params: GridRowParams) =>
          history.push(`/mushroom/home/products/${params.id}`)
        }
      />
    );
  },
);

// ----- Page -----
export default function MushroomProductsPage() {
  const [products, setProducts] = useState<MushroomProduct[] | null>(null);
  const [selected, setSelected] = useState<number[]>([]);
  const [totalTrashed, setTotalTrashed] = useState<number>(0);
  const [busy, setBusy] = useState(false);
  const [trashDialogOpen, setTrashDialogOpen] = useState(false);
  const { handleError } = useContext(ErrorContext);
  const [addOpen, setAddOpen] = useState(false);
  const [addName, setAddName] = useState("");
  const [addStrainNames, setAddStrainNames] = useState<string[]>([]);
  const [addNotes, setAddNotes] = useState("");
  const [addSize, setAddSize] = useState<"small" | "medium" | "large" | "">("");
  const [addWeight, setAddWeight] = useState("");
  const [addWeightUnit, setAddWeightUnit] = useState<"g" | "lb">("g");

  const [showArchivedChecked, setShowArchivedChecked] = useState(
    window.localStorage.getItem(showArchivedCheckedStorageKey) !==
      false.toString(),
  );
  const [showActiveChecked, setShowActiveChecked] = useState(
    window.localStorage.getItem(showActiveCheckedStorageKey) !==
      false.toString(),
  );

  const history = useHistory();
  const isMounted = useRef(true);

  useEffect(() => {
    async function getProducts() {
      setBusy(true);
      try {
        // MVP/local
        let all = loadMushroomProducts();

        if (!all.length) {
          all = [
            {
              id: 1,
              name: "Oyster – 150g Pack",
              size: "small",
              weight: 150,
              weightUnit: "g",
              strainNames: ["Blue Oyster"],
              notes: "Retail label v2",
              archivedAt: null,
            },
            {
              id: 2,
              name: "Lion’s Mane – 150g Pack",
              size: "small",
              weight: 150,
              weightUnit: "g",
              strainNames: ["Lion’s Mane"],
              notes: null,
              archivedAt: null,
            },
            {
              id: 3,
              name: "Mixed Mushrooms – 1lb Bulk",
              size: "large",
              weight: 1,
              weightUnit: "lb",
              strainNames: ["Blue Oyster", "Lion’s Mane"],
              notes: "Wholesale",
              archivedAt: DateTime.local().minus({ days: 7 }).toISO(),
            },
          ];

          saveMushroomProducts(all);
        }

        if (isMounted.current) setProducts(all);
      } catch (error) {
        if (isMounted.current) handleError(error);
      } finally {
        if (isMounted.current) setBusy(false);
      }
    }
    getProducts();
  }, [totalTrashed, handleError]);

  const handleSelectionChange = (m: GridRowSelectionModel): void => {
    let ids: number[] = [];
    const anyM = m as any;
    if (Array.isArray(m)) ids = m.map((v) => Number(v));
    else if (anyM?.ids?.forEach) {
      const t: number[] = [];
      anyM.ids.forEach((v: unknown) => t.push(Number(v)));
      ids = t;
    }
    setSelected(ids);
  };

  const handleClickAdd = () => {
    setAddName("");
    setAddSize("");
    setAddWeight("");
    setAddWeightUnit("g");
    setAddStrainNames([]);
    setAddNotes("");
    setAddOpen(true);
  };

  const handleCloseAdd = () => {
    setAddOpen(false);
  };

  const handleSaveAdd = () => {
    if (!addName.trim()) {
      return;
    }

    const current = loadMushroomProducts();
    const maxId = current.reduce((m, p) => Math.max(m, p.id), 0);

    const parsedWeight =
      addWeight.trim() && !Number.isNaN(Number(addWeight))
        ? Number(addWeight)
        : null;

    const next: MushroomProduct = {
      id: maxId + 1,
      name: addName.trim(),
      strainNames: addStrainNames,
      notes: addNotes.trim() ? addNotes.trim() : null,
      archivedAt: null,
      size: addSize,
      weight: parsedWeight,
      weightUnit: addWeightUnit,
    };

    const updated = [next, ...current];
    saveMushroomProducts(updated);
    setProducts(updated);
    setAddOpen(false);
  };

  const handleClickTrash = () => setTrashDialogOpen(true);

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  const handleTrashDialogClose = async (okTrash?: boolean) => {
    setTrashDialogOpen(false);

    if (!okTrash) return;

    setBusy(true);

    let trashed = 0;
    for (let i = 0; i < selected.length; i++) {
      try {
        trashMushroomProduct(selected[i]);
        trashed++;
        mixpanel.track("Delete Mushroom Product");
      } catch (error) {
        if (isMounted.current) handleError(error);
        break;
      }
    }

    if (isMounted.current) {
      setTotalTrashed((v) => v + trashed);
      setBusy(false);
      setSelected([]);
    }
  };

  const handleShowActiveChanged = (checked: boolean) => {
    setShowActiveChecked(checked);
    window.localStorage.setItem(
      showActiveCheckedStorageKey,
      checked.toString(),
    );
  };

  const handleShowArchivedChanged = (checked: boolean) => {
    setShowArchivedChecked(checked);
    window.localStorage.setItem(
      showArchivedCheckedStorageKey,
      checked.toString(),
    );
  };

  return (
    <Page title={"Products"}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          mb: 2,
          gap: 1,
          flexWrap: "wrap",
        }}
      >
        <Button
          variant="contained"
          color="primary"
          onClick={handleClickAdd}
          disabled={busy}
        >
          Add
        </Button>

        <IconButton
          aria-label="delete"
          disabled={busy || selected.length === 0}
          onClick={handleClickTrash}
          size="large"
        >
          <DeleteIcon />
        </IconButton>

        <ActiveFilterControls
          showActiveChecked={showActiveChecked}
          showArchivedChecked={showArchivedChecked}
          onChangeShowActive={handleShowActiveChanged}
          onChangeShowArchived={handleShowArchivedChanged}
        />
      </Box>

      <Box sx={{ height: 750, width: "100%", pt: 2 }}>
        <ProductsDataGrid
          products={products?.filter(
            (p) =>
              (showActiveChecked && p.archivedAt === null) ||
              (showArchivedChecked && p.archivedAt !== null),
          )}
          onSelectionChange={handleSelectionChange}
          initialPage={getSearchParamNumber(history, "page", 0)}
          onPaginationModelChange={(m) => {
            const params = new URLSearchParams(`page=${m.page}`);
            history.replace({ search: params.toString() });
          }}
        />
      </Box>

      <ConfirmDialog
        title="Trash Products"
        description={`Are you sure you want to trash ${
          selected.length
        } product${selected.length > 1 ? "s" : ""}?`}
        open={trashDialogOpen}
        onClose={handleTrashDialogClose}
      />
      <Dialog open={addOpen} onClose={handleCloseAdd} fullWidth maxWidth="sm">
        <DialogTitle>Add Product</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1, display: "flex", flexDirection: "column", gap: 2 }}>
            <TextField
              label="Name"
              value={addName}
              onChange={(e) => setAddName(e.target.value)}
              fullWidth
            />

            <FormControl fullWidth>
              <InputLabel id="mushroom-product-strains-label">
                Strains
              </InputLabel>
              <Select
                labelId="mushroom-product-strains-label"
                multiple
                value={addStrainNames}
                onChange={(e) => {
                  const v = e.target.value;
                  setAddStrainNames(
                    typeof v === "string" ? v.split(",") : (v as string[]),
                  );
                }}
                input={<OutlinedInput label="Strains" />}
              >
                <MenuItem value="Blue Oyster">Blue Oyster</MenuItem>
                <MenuItem value="Lion’s Mane">Lion’s Mane</MenuItem>
              </Select>
            </FormControl>

            <TextField
              label="Size"
              value={addSize}
              onChange={(e) =>
                setAddSize(e.target.value as "small" | "medium" | "large")
              }
              select
              fullWidth
            >
              <MenuItem value="small">Small</MenuItem>
              <MenuItem value="medium">Medium</MenuItem>
              <MenuItem value="large">Large</MenuItem>
            </TextField>

            <Box sx={{ display: "flex", gap: 2 }}>
              <TextField
                label="Weight"
                value={addWeight}
                onChange={(e) => setAddWeight(e.target.value)}
                fullWidth
              />
              <TextField
                label="Unit"
                value={addWeightUnit}
                type="number"
                onChange={(e) => setAddWeightUnit(e.target.value as "g" | "lb")}
                select
                sx={{ width: 140 }}
              >
                <MenuItem value="g">g</MenuItem>
                <MenuItem value="lb">lb</MenuItem>
              </TextField>
            </Box>

            <TextField
              label="Notes"
              value={addNotes}
              onChange={(e) => setAddNotes(e.target.value)}
              fullWidth
              multiline
              minRows={3}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseAdd} disabled={busy}>
            Cancel
          </Button>
          <Button
            onClick={handleSaveAdd}
            color="primary"
            variant="contained"
            disabled={busy || !addName.trim() || addStrainNames.length === 0}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <LoadingBackdrop open={busy} />
    </Page>
  );
}
